import { errorMessage } from "./auth/authApiLocalClient";
import { findService } from "./auth/discoveryClient";

// nodeowner-api registers itself with discovery-api as service_name "api"
// (see its own main.rs) -- every route on it requires the nodeowner_session
// cookie (unlike kwms's own wh-api, whose GET /wh is deliberately public),
// so every fetch below needs credentials: "include".

export interface Warehouse {
  whUid: string;
  nodeUid: string;
  nodeName: string;
  nodeAddress: string;
  timeZone: string;
}

interface WarehouseWire {
  wh_uid: string;
  node_uid: string;
  node_name: string;
  node_address: string;
  time_zone: string;
}

function fromWire(w: WarehouseWire): Warehouse {
  return { whUid: w.wh_uid, nodeUid: w.node_uid, nodeName: w.node_name, nodeAddress: w.node_address, timeZone: w.time_zone };
}

/** `GET /wh` -- every declared warehouse for this owner. */
export async function fetchWarehouses(): Promise<Warehouse[]> {
  const api = await findService("api");
  const response = await fetch(`${api.base_url}/wh`, { credentials: "include" });
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Не удалось получить список складов"));
  }
  const body: { warehouses: WarehouseWire[] } = await response.json();
  return body.warehouses.map(fromWire);
}

/** `POST /wh` -- declares a new warehouse, minimum MGR (enforced in nodeowner-pg). */
export async function createWarehouse(nodeName: string, nodeAddress: string, timeZone: string): Promise<Warehouse> {
  const api = await findService("api");
  const response = await fetch(`${api.base_url}/wh`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ node_name: nodeName, node_address: nodeAddress, time_zone: timeZone }),
  });
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Не удалось создать склад"));
  }
  const body: WarehouseWire = await response.json();
  return fromWire(body);
}

export interface OnboardingRequest {
  requestUid: string;
  userUid: string;
  phone: string;
  createdAt: string;
}

interface OnboardingRequestWire {
  customeronboardingrequest_uid: string;
  user_uid: string;
  phone: string;
  request_status: string;
  request_details: unknown;
  created_at: string;
}

function onboardingFromWire(r: OnboardingRequestWire): OnboardingRequest {
  return { requestUid: r.customeronboardingrequest_uid, userUid: r.user_uid, phone: r.phone, createdAt: r.created_at };
}

/** `GET /inbox/pending` -- the customer-onboarding request queue, oldest first. */
export async function fetchPendingOnboardingRequests(): Promise<OnboardingRequest[]> {
  const api = await findService("api");
  const response = await fetch(`${api.base_url}/inbox/pending`, { credentials: "include" });
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Не удалось получить заявки"));
  }
  const body: { requests: OnboardingRequestWire[] } = await response.json();
  return body.requests.map(onboardingFromWire);
}

/** `POST /inbox/{uid}/approve` -- minimum MGR. Creates the new `customers.customer` row. */
export async function approveOnboardingRequest(requestUid: string): Promise<void> {
  const api = await findService("api");
  const response = await fetch(`${api.base_url}/inbox/${requestUid}/approve`, {
    method: "POST",
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Не удалось одобрить заявку"));
  }
}

/** `POST /inbox/{uid}/reject` -- minimum MGR. No `customers.customer` row gets created. */
export async function rejectOnboardingRequest(requestUid: string, rejectReason: string): Promise<void> {
  const api = await findService("api");
  const response = await fetch(`${api.base_url}/inbox/${requestUid}/reject`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ reject_reason: rejectReason }),
  });
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Не удалось отклонить заявку"));
  }
}
